<script>
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';
import TemplateCollections from '@/components/frames/TemplateCollections.vue';
import TemplateCollectionFrames from '@/components/frames/TemplateCollectionFrames.vue';
import TemplateFrameDetails from '@/components/frames/TemplateFrameDetails.vue';
import FrameDetailsTab from '@/components/frames/details/FrameDetailsTab.vue';
import { mapActions, mapState } from 'vuex';
import FrameHint from '@/components/frames/hint/FrameHint.vue';
import FramesColor from '@/components/frames/color/FramesColor.vue';
import FramesSizes from '@/components/frames/sizes/FramesSizes.vue';
import {
  FolderOutlined,
  PictureOutlined,
  SettingOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  BgColorsOutlined,
  ColumnWidthOutlined,
  MenuOutlined,
  CloseOutlined,
} from '@ant-design/icons-vue';

const b = bem('create-page');
export default {
  components: {
    FramesSizes,
    FramesColor,
    FrameHint,
    PageHeader,
    TemplateCollections,
    TemplateCollectionFrames,
    TemplateFrameDetails,
    FrameDetailsTab,
    FolderOutlined,
    PictureOutlined,
    SettingOutlined,
    FileTextOutlined,
    QuestionCircleOutlined,
    BgColorsOutlined,
    ColumnWidthOutlined,
    MenuOutlined,
    CloseOutlined,
  },
  data() {
    return {
      activeKey: 'collections',
      selectedCollection: {},
      selectedFrameId: 0,
      show: false,
      isNew: false,
      isShowOptions: true,
      isSidebarOpen: false,
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getTemplateCollectionsFramesAction: 'createTemplate/getTemplateCollectionsFrames',
    }),
    getTitle() {
      if (this.selectedCollection.id && this.activeKey === 'frames') {
        return `${this.selectedCollection.name} frames`;
      }
      if (this.frameDetails && this.frameDetails.id && this.frameOptions.length > 0 && this.activeKey === 'options') {
        return `${this.frameDetails.displayName} details`;
      }
      return 'Photo art templates';
    },
    setActiveKey(key) {
      this.activeKey = key;
      this.isSidebarOpen = false;
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    closeSidebar() {
      this.isSidebarOpen = false;
    },
  },
  computed: {
    ...mapState({
      frameOptions: (state) => state.createTemplate.frameOptions,
      frameDetails: (state) => state.createTemplate.frameDetails,
    }),
    isFramesDisabled() {
      return !this.selectedCollection.id;
    },
    isOptionsDisabled() {
      return !Object.keys(this.frameDetails).length;
    },
    isFrameTabsDisabled() {
      return !this.selectedFrameId;
    },
  },
  mounted() {
    this.getTemplateCollectionsFramesAction();
  },
  watch: {
    selectedCollection: {
      handler(newValue, oldValue) {
        if (newValue && newValue.id !== oldValue.id) {
          this.activeKey = 'frames';
        }
      },
      deep: true,
    },
    frameDetails: {
      handler(newValue, oldValue) {
        if (newValue && Object.keys(newValue).length && newValue.id !== oldValue.id) {
          this.activeKey = 'options';
          this.isShowOptions = true;
        }
      },
      deep: true,
    },
  },
};
</script>

<template>
  <PageHeader :title="getTitle()"/>

  <!-- Mobile menu button -->
  <button :class="b('mobile-menu-btn')" @click="toggleSidebar">
    <menu-outlined v-if="!isSidebarOpen" />
    <close-outlined v-else />
  </button>

  <!-- Overlay for mobile -->
  <div
    v-if="isSidebarOpen"
    :class="b('overlay')"
    role="button"
    tabindex="0"
    @click="closeSidebar"
    @keydown.enter="closeSidebar"
    @keydown.escape="closeSidebar"
  />

  <div :class="b()">
    <aside :class="b('sidebar', { open: isSidebarOpen })">
      <nav :class="b('nav')">
        <!-- Collections -->
        <div
          :class="b('nav-item', { active: activeKey === 'collections' })"
          role="button"
          tabindex="0"
          @click="setActiveKey('collections')"
          @keydown.enter="setActiveKey('collections')"
        >
          <folder-outlined :class="b('nav-icon')" />
          <span :class="b('nav-label')">Collections</span>
        </div>

        <!-- Frames -->
        <div
          :class="b('nav-item', {
            active: activeKey === 'frames',
            disabled: isFramesDisabled
          })"
          role="button"
          tabindex="0"
          :aria-disabled="isFramesDisabled"
          @click="!isFramesDisabled && setActiveKey('frames')"
          @keydown.enter="!isFramesDisabled && setActiveKey('frames')"
        >
          <picture-outlined :class="b('nav-icon')" />
          <span :class="b('nav-label')">Frames</span>
        </div>

        <!-- Nested items under Frames -->
        <div :class="b('nav-children')">
          <div
            :class="b('nav-item', {
              child: true,
              active: activeKey === 'options',
              disabled: isOptionsDisabled
            })"
            role="button"
            tabindex="0"
            :aria-disabled="isOptionsDisabled"
            @click="!isOptionsDisabled && setActiveKey('options')"
            @keydown.enter="!isOptionsDisabled && setActiveKey('options')"
          >
            <setting-outlined :class="b('nav-icon')" />
            <span :class="b('nav-label')">Options</span>
          </div>
          <div
            :class="b('nav-item', {
              child: true,
              active: activeKey === 'details',
              disabled: isFrameTabsDisabled
            })"
            role="button"
            tabindex="0"
            :aria-disabled="isFrameTabsDisabled"
            @click="!isFrameTabsDisabled && setActiveKey('details')"
            @keydown.enter="!isFrameTabsDisabled && setActiveKey('details')"
          >
            <file-text-outlined :class="b('nav-icon')" />
            <span :class="b('nav-label')">Details</span>
          </div>
          <div
            :class="b('nav-item', {
              child: true,
              active: activeKey === 'hint',
              disabled: isFrameTabsDisabled
            })"
            role="button"
            tabindex="0"
            :aria-disabled="isFrameTabsDisabled"
            @click="!isFrameTabsDisabled && setActiveKey('hint')"
            @keydown.enter="!isFrameTabsDisabled && setActiveKey('hint')"
          >
            <question-circle-outlined :class="b('nav-icon')" />
            <span :class="b('nav-label')">Learn More</span>
          </div>
        </div>

        <!-- Divider -->
        <div :class="b('nav-divider')" />

        <!-- Colors -->
        <div
          :class="b('nav-item', { active: activeKey === 'color' })"
          role="button"
          tabindex="0"
          @click="setActiveKey('color')"
          @keydown.enter="setActiveKey('color')"
        >
          <bg-colors-outlined :class="b('nav-icon')" />
          <span :class="b('nav-label')">Colors</span>
        </div>

        <!-- Sizes -->
        <div
          :class="b('nav-item', { active: activeKey === 'sizes' })"
          role="button"
          tabindex="0"
          @click="setActiveKey('sizes')"
          @keydown.enter="setActiveKey('sizes')"
        >
          <column-width-outlined :class="b('nav-icon')" />
          <span :class="b('nav-label')">Sizes</span>
        </div>
      </nav>
    </aside>

    <main :class="b('content')">
      <TemplateCollections
        v-if="activeKey === 'collections'"
        :selectedCollection="selectedCollection"
        v-model:selected-collection="selectedCollection"
      />
      <TemplateCollectionFrames
        v-if="activeKey === 'frames'"
        :selectedCollection="selectedCollection"
        v-model:selected-frame-id="selectedFrameId"
      />
      <TemplateFrameDetails
        v-if="activeKey === 'options'"
        v-model:isShowOptions="isShowOptions"
        :selected-collection="selectedCollection"
      />
      <FrameDetailsTab
        v-if="activeKey === 'details'"
        :frame-id="selectedFrameId"
      />
      <FrameHint
        v-if="activeKey === 'hint'"
        :id="selectedFrameId"
      />
      <FramesColor
        v-if="activeKey === 'color'"
        :selectedMaterialId="selectedCollection.id"
      />
      <FramesSizes
        v-if="activeKey === 'sizes'"
      />
    </main>
  </div>
</template>

<style src="./style.scss" scoped lang="scss"></style>
